import React, { useState } from 'react';
import axios from 'axios';
import { Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ label, value, testId }) => (
  <div><p className="text-xs text-zinc-500">{label}</p><p className="font-semibold text-white" data-testid={testId}>{value}</p></div>
);

const ExcludedList = ({ title, rows }) => (rows?.length ? (
  <details className="mt-2 text-xs text-zinc-400"><summary className="cursor-pointer">{title} ({rows.length})</summary>
    <ul className="mt-1 space-y-0.5">{rows.map((r, i) => <li key={`${r.id}-${i}`}>{r.name || '—'} · {r.email} · {r.postal_code}</li>)}</ul>
  </details>
) : null);

export const MarketingLaunchPanel = ({ token }) => {
  const [report, setReport] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const call = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (error) { toast.error(error.response?.data?.detail || 'Opération impossible.'); } finally { setBusy(false); }
  };
  const load = () => call(async () => setReport((await axios.get(`${API}/admin/marketing/launch-paris/report`, { headers })).data));
  const testSend = () => call(async () => {
    const { data } = await axios.post(`${API}/admin/marketing/launch-paris/test-send`, { email: testEmail }, { headers });
    toast[data.sent ? 'success' : 'info'](data.sent ? 'Annonce envoyée au compte de test.' : data.detail);
  });
  const send = () => call(async () => {
    const preview = (await axios.post(`${API}/admin/marketing/launch-paris/send`, { confirm: false }, { headers })).data;
    if (!preview.to_send) { toast.info('Aucun usager consentant à informer pour le moment.'); return; }
    if (!window.confirm(`Envoyer l’annonce à ${preview.to_send} usager(s) ayant accepté les offres ? Chaque usager ne la reçoit qu’une fois.`)) return;
    await axios.post(`${API}/admin/marketing/launch-paris/send`, { confirm: true }, { headers });
    toast.success(`Diffusion lancée vers ${preview.to_send} usager(s).`);
    setTimeout(load, 4000);
  });

  return (
    <section className="mb-6 rounded-lg border border-zinc-800 bg-[#18181B] p-5" data-testid="marketing-launch-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-bold text-white"><Megaphone className="h-5 w-5 text-[#FFD60A]" />Annonce Paris / petite couronne (usagers consentants)</h2>
        <Button variant="outline" onClick={load} disabled={busy} data-testid="marketing-report-btn">Rapport des destinataires</Button>
      </div>
      {report && (
        <div data-testid="marketing-report">
          <div className="grid gap-3 text-sm sm:grid-cols-4">
            <Stat label="Candidats 75/92/93/94" value={report.zone_count} testId="marketing-zone-count" />
            <Stat label="Lignes export CSV" value={report.csv_export_count} testId="marketing-csv-count" />
            <Stat label="Éligibles après exclusions" value={report.eligible_count} testId="marketing-eligible-count" />
            <Stat label="Déjà informés" value={report.already_sent} testId="marketing-already-sent" />
            <Stat label="Consentements acceptés" value={report.consent.accepted} testId="marketing-consent-accepted" />
            <Stat label="Refusés" value={report.consent.refused} testId="marketing-consent-refused" />
            <Stat label="Sans réponse" value={report.consent.no_answer} testId="marketing-consent-none" />
            <Stat label="Push actif (web/Android · iPhone)" value={`${report.push.web_or_android} · ${report.push.ios_app}`} testId="marketing-push-count" />
          </div>
          <p className="mt-3 text-xs text-zinc-500">{report.reconciliation}</p>
          <ExcludedList title="Comptes désactivés" rows={report.deactivated} />
          <ExcludedList title="Comptes de test" rows={report.tests} />
          <ExcludedList title="Doublons" rows={report.duplicates} />
          <ExcludedList title="Inscriptions de la zone (30 derniers jours)" rows={report.recent_zone_signups} />
          <ExcludedList title="Suppressions de compte (30 derniers jours)" rows={report.recent_account_deletions.map((d) => ({ id: d.created_at, email: d.email_at_delete, postal_code: new Date(d.created_at).toLocaleString('fr-FR') }))} />
          <div className="mt-4 grid gap-2 md:grid-cols-[1fr_auto_auto]">
            <Input placeholder="Email d’un compte de test ayant accepté" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="marketing-test-email-input" />
            <Button variant="outline" onClick={testSend} disabled={busy || !testEmail} data-testid="marketing-test-send-btn">Tester sur ce compte</Button>
            <Button onClick={send} disabled={busy} className="bg-[#FFD60A] font-bold text-black hover:bg-[#E6C209]" data-testid="marketing-send-btn">Diffuser aux usagers consentants</Button>
          </div>
        </div>
      )}
    </section>
  );
};
